import { atom } from 'jotai';

import type { CrawlingData, CrawlingProduct, StatusCodes } from '../types';
import { CrawlingProductStatus } from '../types';
import { getProductOptionErrors } from '../utils';

export const crawlingProductsState = atom<CrawlingData | undefined>(undefined);

/**
 * Live map of rows whose options are inconsistent within their variant group
 * (row object → error code), derived from the current products. Computed from
 * the `options` data so it survives the status resets between setup steps.
 */
export const productOptionErrorsState = atom((get) => {
  const data = get(crawlingProductsState);
  const errors = new Map<CrawlingProduct, StatusCodes>();
  data?.products.forEach((product) => {
    for (const [row, code] of getProductOptionErrors(product)) {
      errors.set(row, code);
    }
  });
  return errors;
});

/** Number of products that have at least one inconsistent-options error. */
export const productOptionErrorsCountState = atom(
  (get) =>
    get(crawlingProductsState)?.products.filter(
      (product) => getProductOptionErrors(product).length > 0,
    ).length ?? 0,
);

export const productsProcessedCountState = atom((get) => {
  const crawlingProducts = get(crawlingProductsState);

  return (
    crawlingProducts?.products.filter(
      (entry) =>
        entry._status === CrawlingProductStatus.SUCCESS ||
        entry._status === CrawlingProductStatus.ERROR,
    ) ?? []
  ).length;
});

export const productsProcessedErrorCountState = atom((get) => {
  const crawlingProducts = get(crawlingProductsState);

  return (
    crawlingProducts?.products.filter(
      (entry) => entry._status === CrawlingProductStatus.ERROR,
    ) ?? []
  ).length;
});

export const productsProcessingState = atom((get) => {
  const crawlingProducts = get(crawlingProductsState);

  return (
    (
      crawlingProducts?.products.filter(
        (entry) => entry._status === CrawlingProductStatus.IN_PROGRESS,
      ) ?? []
    ).length !== 0
  );
});

export const productsProcessableState = atom((get) => {
  const crawlingProducts = get(crawlingProductsState);

  return (
    crawlingProducts?.products.filter(
      (entry) => entry._status !== CrawlingProductStatus.NOT_STARTED,
    ) ?? []
  ).length;
});
